import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';

// Fix leaflet default marker icon paths broken by bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

/** Reistijd-zones (hemelsbreed vanaf KvK-kantoor) — moet gelijk lopen met
 *  TRAVEL_ZONES in src/services/travelTimeService.ts op de backend. */
const TRAVEL_ZONE_RINGS = [
  { zone: 1, maxKm: 15, forfaitUren: 0.5, color: '#16a34a' }, // groen
  { zone: 2, maxKm: 45, forfaitUren: 1.0, color: '#eab308' }, // geel
  { zone: 3, maxKm: 75, forfaitUren: 1.5, color: '#f97316' }, // oranje
  { zone: 4, maxKm: null as number | null, forfaitUren: 2.0, color: '#dc2626' }, // rood (>75)
];

const NL_CENTER: [number, number] = [52.2, 5.3];
const RADIUS_MIN = 10;
const RADIUS_MAX = 100;

interface BusinessLocation {
  available: boolean;
  postcode?: string;
  lat?: number;
  lon?: number;
}

interface ReisradiusMapProps {
  /** Reisradius in km; null = heel Nederland */
  radiusKm: number | null;
  onRadiusChange: (radiusKm: number | null) => void;
}

/** Herzoomt de kaart wanneer radius of centrum verandert */
function FitToRadius({ center, radiusKm }: { center: [number, number]; radiusKm: number | null }) {
  const map = useMap();
  const [lat, lon] = center;
  useEffect(() => {
    if (radiusKm == null) {
      // Heel Nederland
      map.setView(NL_CENTER, 7);
      return;
    }
    const bounds = L.latLng(lat, lon).toBounds(radiusKm * 2 * 1000);
    map.fitBounds(bounds, { padding: [20, 20] });
  }, [map, lat, lon, radiusKm]);
  return null;
}

/**
 * Dynamische Reisradius — slider + kaart in het professional-portfolio.
 * Toont het KvK-kantooradres als middelpunt, de eigen werkgebied-cirkel
 * (beweegt live mee met de slider) en de reistijd-zones als gekleurde ringen.
 */
export default function ReisradiusMap({ radiusKm, onRadiusChange }: ReisradiusMapProps) {
  const { t } = useTranslation('profile');
  const [location, setLocation] = useState<BusinessLocation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/professional/business-location')
      .then((res) => {
        if (!cancelled) setLocation(res.data?.data || { available: false });
      })
      .catch(() => {
        if (!cancelled) setLocation({ available: false });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const heelNederland = radiusKm == null;
  const sliderValue = radiusKm ?? RADIUS_MAX;
  const hasPoint = !!(location?.available && location.lat != null && location.lon != null);
  const center: [number, number] = hasPoint ? [location!.lat!, location!.lon!] : NL_CENTER;

  return (
    <div>
      {/* Slider + heel-Nederland toggle */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            {t('reisradius.sliderLabel')}
          </span>
          <span className="text-sm font-semibold text-blue-700">
            {heelNederland ? t('reisradius.wholeCountry') : `${sliderValue} km`}
          </span>
        </div>
        <input
          type="range"
          min={RADIUS_MIN}
          max={RADIUS_MAX}
          step={5}
          value={sliderValue}
          disabled={heelNederland}
          onChange={(e) => onRadiusChange(parseInt(e.target.value, 10))}
          className={`w-full accent-blue-600 ${heelNederland ? 'opacity-40' : ''}`}
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>{RADIUS_MIN} km</span>
          <span>{RADIUS_MAX} km</span>
        </div>
        <label className="mt-2 flex items-center text-sm">
          <input
            type="checkbox"
            checked={heelNederland}
            onChange={(e) => onRadiusChange(e.target.checked ? null : 50)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
          />
          {t('reisradius.wholeCountryLabel')}
        </label>
      </div>

      {/* Kaart */}
      {loading ? (
        <div className="h-72 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-500">
          {t('reisradius.loadingMap')}
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: '320px' }}>
          <MapContainer center={center} zoom={hasPoint ? 9 : 7} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {hasPoint && (
              <>
                <FitToRadius center={center} radiusKm={heelNederland ? null : sliderValue} />
                <Marker position={center}>
                  <Popup>
                    {t('reisradius.officeMarker')}{location?.postcode ? ` (${location.postcode})` : ''}
                  </Popup>
                </Marker>
                {/* Reistijd-zone ringen (15/45/75 km) */}
                {TRAVEL_ZONE_RINGS.filter((z) => z.maxKm != null).map((z) => (
                  <Circle
                    key={z.zone}
                    center={center}
                    radius={(z.maxKm as number) * 1000}
                    pathOptions={{ color: z.color, weight: 1.5, dashArray: '6 6', fillOpacity: 0.03 }}
                  />
                ))}
                {/* Eigen werkgebied-cirkel — beweegt live mee met de slider */}
                {!heelNederland && (
                  <Circle
                    center={center}
                    radius={sliderValue * 1000}
                    pathOptions={{ color: '#2563eb', weight: 2.5, fillColor: '#3b82f6', fillOpacity: 0.12 }}
                  />
                )}
              </>
            )}
          </MapContainer>
        </div>
      )}

      {!loading && !hasPoint && (
        <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          {t('reisradius.noLocation')}
        </p>
      )}

      {/* Zone-legenda */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        {TRAVEL_ZONE_RINGS.map((z) => (
          <div key={z.zone} className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200">
            <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: z.color }} />
            <span className="text-gray-700">
              {t(`reisradius.zone${z.zone}Name`)}{' '}
              <span className="text-gray-500">
                ({z.maxKm != null ? `< ${z.maxKm} km` : '> 75 km'} · {z.forfaitUren.toLocaleString('nl-NL')} {t('reisradius.hourUnit')})
              </span>
            </span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-gray-500">{t('reisradius.zoneExplainer')}</p>
    </div>
  );
}
